// src/pages/sow-draft/useSowDraft.js
//
// All state, derived values, and handlers for the Draft SOW 3-step wizard.
// Split out of SowDraftPage.jsx so the page and step components are
// composition/markup only.
//
// Wired to the real backend (sow_draft_controller.py). Real field names
// differ from what this hook used to assume: a draft's id is `draft_id`
// (not `id`), its sections are `sections` (not `sections_json`) keyed by
// `section_id` (not `id`), templates are keyed by `template_id`, and
// versions are keyed by a "major.minor" string (no `version_id`). There's
// no current-version endpoint (derived from getVersions()'s last entry), no
// fine-grained generation progress (only a single status string), no
// Salesforce-status pre-check endpoint, and generate/regenerate accept no
// body -- the resources/milestones/pricing wizard state and regenerate
// "instructions" stay local-only UI state, matching what's actually sent.
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDraftInputPersistence } from "./useDraftInputPersistence";
import { getApiError, sowDraftApi } from "../../api/client";
import {
  getGoogleDriveAccessToken,
  uploadDocxAsGoogleDoc,
} from "../../utils/googleDrivePublish";
import { useAuth } from "../../context/AuthContext";
import { useWorkspace } from "../../context/WorkspaceContext";
import {
  CONTRACT_TYPES,
  calculateHoursFromDates,
  detectContractType,
  mapJossToResources,
  normalizeList,
} from "./sowDraftHelpers";

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;

// No projectName field here -- Project Name is now shown read-only, sourced
// from the currently-selected Project (Engagement) in the topbar, the same
// way Client is already read-only from the selected Opportunity. It's no
// longer a free-text duplicate of "Project / client name" collected when
// the project itself was registered.
const emptyProjectDetails = {
  projectContext: "",
  inScope: "",
  outOfScope: "",
  assumptions: "",
  deliverables: "",
  startDate: "",
  endDate: "",
  milestones: [{ name: "", deliverable: "", dueDate: "", percentage: 0 }],
  monthlyFee: 0,
  duration: 12,
  resources: [
    {
      role: "",
      level: "",
      location: "",
      count: 1,
      hours: 0,
      rate: 0,
      startDate: "",
      endDate: "",
      technology: "",
      domain: "",
      sellingAmount: 0,
      status: "",
    },
  ],
};

const ACCEPTED_PROPOSAL_TYPES =
  ".pdf,.docx,.pptx,.xlsx,.xls,.txt,.csv,.png,.jpg,.jpeg,.gif,.bmp,.tiff";

export function useSowDraft() {
  const { currentAccount, createEngagement } = useWorkspace();
  const { hasGlobalPermission, hasPermission } = useAuth();

  const [step, setStep] = useState(1);
  const [opps, setOpps] = useState([]);
  const [oppSearch, setOppSearch] = useState("");
  const [oppLoading, setOppLoading] = useState(false);
  const [selectedOpp, setSelectedOpp] = useState(null);
  const [draftsMap, setDraftsMap] = useState({});
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");

  const [projectDetails, setProjectDetails] = useState(emptyProjectDetails);

  // Survives a refresh or navigating away mid-wizard; scoped per
  // opportunity. See useDraftInputPersistence for why the server copy
  // cannot be used for this yet.
  const { restore: restoreSavedInputs, clear: clearSavedInputs } =
    useDraftInputPersistence({
      opportunityId: selectedOpp?.id,
      projectDetails,
      templateId: selectedTemplateId,
    });

  const [jossLoading, setJossLoading] = useState(false);

  const [proposalFiles, setProposalFiles] = useState([]);
  const [proposalParsed, setProposalParsed] = useState(null);
  const [proposalUploading, setProposalUploading] = useState(false);
  const [proposalError, setProposalError] = useState("");

  const [draft, setDraft] = useState(null);
  const [selectedSectionId, setSelectedSectionId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [genProgress, setGenProgress] = useState({
    current: 0,
    total: 0,
    section: "",
    percent: 0,
    startedAt: null,
  });
  const [msg, setMsg] = useState({ type: "", text: "" });
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [regenModalOpen, setRegenModalOpen] = useState(false);
  const [regenInstructions, setRegenInstructions] = useState("");
  const [publishingGdoc, setPublishingGdoc] = useState(false);
  const [gdocLink, setGdocLink] = useState("");

  const [currentVersion, setCurrentVersion] = useState("1.0");
  const [versionModalOpen, setVersionModalOpen] = useState(false);
  const [sfModalOpen, setSfModalOpen] = useState(false);
  const [newProjectModalOpen, setNewProjectModalOpen] = useState(false);

  const contractType = useMemo(
    () => detectContractType(selectedOpp?.contract_type),
    [selectedOpp],
  );

  // Mirrors ProjectsPage.jsx's own gating for the same action -- an
  // org-wide holder can register a project regardless of which Account is
  // selected; an account-scoped holder only if their grant covers this one.
  const canCreateProject = Boolean(
    currentAccount &&
    (hasGlobalPermission("conductor:engagement:create") ||
      hasPermission("conductor:engagement:create", {
        accountId: currentAccount.account_id,
      })),
  );

  // Same backend call as ProjectsPage.jsx's handleRegister -- this is the
  // exact same RegisterProjectModal, just reachable from here too. Since an
  // Opportunity is already selected, the caller passes it through as
  // presetContractType/presetOpportunityId, so the modal skips its own
  // pickers and payload.opportunityId already carries selectedOpp.id --
  // this carries it straight onto the new Engagement (see
  // EngagementAggregate's own opportunity_id field).
  const registerProject = useCallback(
    async (payload) => {
      const created = await createEngagement({
        client_name: payload.name,
        platform: payload.platform,
        contract_type: payload.contractType,
        opportunity_id: payload.opportunityId,
      });
      return { ...created, name: created.client_name };
    },
    [createEngagement],
  );

  const filteredOpps = useMemo(() => {
    if (!oppSearch.trim()) return opps;
    const q = oppSearch.toLowerCase();
    return opps.filter(
      (o) =>
        (o.opportunity_name || "").toLowerCase().includes(q) ||
        (o.account_name || "").toLowerCase().includes(q) ||
        (o.contract_type || "").toLowerCase().includes(q) ||
        (o.stage || "").toLowerCase().includes(q) ||
        (o.sf_opportunity_id || "").toLowerCase().includes(q),
    );
  }, [opps, oppSearch]);

  const resourceTotals = useMemo(() => {
    let totalHours = 0,
      totalAmount = 0;
    const breakdown = projectDetails.resources.map((r) => {
      const amount =
        r.sellingAmount || (r.count || 1) * (r.hours || 0) * (r.rate || 0);
      totalHours += (r.count || 1) * (r.hours || 0);
      totalAmount += amount;
      return { ...r, amount };
    });
    return { breakdown, totalHours, totalAmount };
  }, [projectDetails.resources]);

  const milestoneTotals = useMemo(() => {
    const total = projectDetails.milestones.reduce(
      (sum, m) => sum + (parseFloat(m.percentage) || 0),
      0,
    );
    return { total, isValid: Math.abs(total - 100) < 0.01 };
  }, [projectDetails.milestones]);

  const grandTotal = useMemo(() => {
    if (contractType === CONTRACT_TYPES.MANAGED) {
      return (projectDetails.monthlyFee || 0) * (projectDetails.duration || 1);
    }
    return resourceTotals.totalAmount;
  }, [contractType, projectDetails, resourceTotals]);

  const selectedSection = useMemo(() => {
    const secs = draft?.sections || [];
    return secs.find((s) => s.section_id === selectedSectionId) || secs[0];
  }, [draft, selectedSectionId]);

  const orderedSections = useMemo(() => {
    return [...(draft?.sections || [])].sort(
      (a, b) => (a.order || 0) - (b.order || 0),
    );
  }, [draft]);

  const showMsg = (type, text) => {
    setMsg({ type, text });
    if (type === "success")
      setTimeout(() => setMsg({ type: "", text: "" }), 4000);
  };

  const loadVersionInfo = useCallback(async () => {
    if (!draft) return;
    try {
      // No dedicated current-version endpoint -- derive it from the last
      // entry of the version history instead.
      const versions = await sowDraftApi.getVersions(draft.draft_id);
      const list = Array.isArray(versions)
        ? versions
        : versions?.versions || [];
      setCurrentVersion(list[list.length - 1]?.version || "1.0");
    } catch (e) {
      console.error("Failed to load version:", e);
    }
  }, [draft]);

  useEffect(() => {
    if (draft) loadVersionInfo();
  }, [draft, loadVersionInfo]);

  useEffect(() => {
    let ignore = false;
    (async () => {
      setOppLoading(true);
      try {
        const res = await sowDraftApi.listOpps(1, 100);
        // Real fields (name, sfdc_account_id, salesforce_opportunity_id,
        // deal_value) differ from what this page's components read --
        // normalize once here rather than touching every read site.
        // account_name comes from the API (opportunity_controller.py's
        // _dto_to_opp_response) -- this used to overwrite it with an
        // em-dash on the belief that only a bare sfdc_account_id was
        // available, and that dash reached the drafted SOW as
        // "SOW - — - <project>" in eight sections of one live run.
        const oppList = normalizeList(res?.items || []).map((o) => ({
          ...o,
          opportunity_name: o.name,
          account_name:
            o.account_name ||
            (o.sfdc_account_id ? `Account #${o.sfdc_account_id}` : "—"),
          sf_opportunity_id: o.salesforce_opportunity_id,
          opportunity_amount: o.deal_value,
        }));
        if (!ignore) setOpps(oppList);

        try {
          // Bare array, keyed by draft_id (no `id` field on this DTO).
          const draftsList = normalizeList(await sowDraftApi.listDrafts());
          const map = {};
          draftsList.forEach((d) => {
            if (d.opportunity_id) {
              if (
                !map[d.opportunity_id] ||
                d.draft_id > map[d.opportunity_id].draft_id
              ) {
                map[d.opportunity_id] = d;
              }
            }
          });
          if (!ignore) setDraftsMap(map);
        } catch (e) {
          console.log("Could not load drafts list:", e);
        }
      } catch (e) {
        if (!ignore) showMsg("error", getApiError(e));
      } finally {
        if (!ignore) setOppLoading(false);
      }
    })();
    return () => {
      ignore = true;
    };
  }, []);

  const fetchTemplates = useCallback(async () => {
    if (!selectedOpp) return;
    try {
      // Filter by the normalized contract type (T&M / Fixed Bid / Managed
      // Services), not the Opportunity's raw text (e.g. "Time and
      // Material") -- that raw string never matches a template's stored
      // contract_type, so filtering on it always came back empty and fell
      // through to showing every template regardless of contract type.
      // Real list items are {template_id, name, contract_type} only -- no
      // version/sections_json on the list (that's fine, only id+name are
      // needed for this dropdown).
      let list = normalizeList(await sowDraftApi.listTemplatesV2(contractType));
      if (!list.length) {
        list = normalizeList(await sowDraftApi.listTemplatesV2(""));
      }
      setTemplates(list);
      if (list.length === 1) setSelectedTemplateId(String(list[0].template_id));
    } catch (e) {
      showMsg("error", getApiError(e));
    }
  }, [selectedOpp, contractType]);

  useEffect(() => {
    if (selectedOpp) {
      fetchTemplates();
      setProjectDetails((prev) => ({
        ...prev,
        startDate: selectedOpp.project_start_date || "",
      }));
    }
  }, [selectedOpp, fetchTemplates]);

  const selectOpportunity = (opp) => {
    setSelectedOpp(opp);
    setDraft(null);
    setGdocLink("");
    setCurrentVersion("1.0");

    // Pick up anything typed for this opportunity in an earlier visit.
    // Falls back to a clean form, so a first visit behaves as before.
    const saved = restoreSavedInputs(opp?.id);
    setProjectDetails(saved?.projectDetails || emptyProjectDetails);
    setSelectedTemplateId(saved?.templateId || "");
    if (saved)
      showMsg("info", "Restored your unsaved inputs for this opportunity.");

    setStep(2);
  };

  const loadExistingDraft = async (opp) => {
    const existingDraft = draftsMap[opp.id];
    if (!existingDraft) return;

    setBusy(true);
    try {
      const d = await sowDraftApi.getDraft(existingDraft.draft_id);
      setSelectedOpp(opp);
      setDraft(d);
      setSelectedSectionId(d?.sections?.[0]?.section_id || null);
      setStep(3);
      showMsg("success", "Loaded existing draft");
    } catch (e) {
      showMsg("error", getApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const goBack = () => {
    if (step === 2) {
      setStep(1);
      setSelectedOpp(null);
    } else if (step === 3) setStep(2);
  };

  const buildUserInputs = () => {
    // The resources table starts with one blank row, and the resource plan
    // is optional. Serializing a blank row produced
    // " (N/A, N/A): 1 × 0 hrs × 0/hr", which the model reproduced verbatim
    // in the Resource Plan section of a real SOW. An omitted plan must look
    // omitted, not like a row of zeroes.
    const namedResources = (resourceTotals.breakdown || []).filter((r) =>
      (r.role || "").trim(),
    );
    const base = {
      project_name: selectedOpp?.opportunity_name,
      project_context: projectDetails.projectContext,
      in_scope: projectDetails.inScope,
      scope: projectDetails.inScope,
      out_of_scope: projectDetails.outOfScope,
      assumptions: projectDetails.assumptions,
      deliverables: projectDetails.deliverables,
      start_date: projectDetails.startDate || selectedOpp?.project_start_date,
      end_date: projectDetails.endDate,
      client_name: selectedOpp?.account_name,
      contract_type: contractType,
      total_amount: grandTotal,
      total_price: grandTotal,
      total_hours: resourceTotals.totalHours,
      resource_plan: namedResources
        .map(
          (r) =>
            `${r.role} (${r.level || "N/A"}, ${r.location || "N/A"}${r.technology ? `, ${r.technology}` : ""}${r.domain ? `, ${r.domain}` : ""}): ${r.count} × ${r.hours} hrs × ${r.rate}/hr`,
        )
        .join("\n"),
      resources: namedResources,
      proposal_context: proposalParsed?.combined_text || "",
    };

    if (contractType === CONTRACT_TYPES.FIXED_BID) {
      const validMilestones = projectDetails.milestones.filter((m) => m.name);
      return {
        ...base,
        pricing_model: "Fixed Bid",
        milestones: validMilestones,
      };
    } else if (contractType === CONTRACT_TYPES.TM) {
      return { ...base, pricing_model: "Time & Material" };
    }
    return {
      ...base,
      pricing_model: "Managed Services",
      monthly_fee: projectDetails.monthlyFee,
      duration_months: projectDetails.duration,
      contract_value: grandTotal,
    };
  };

  const createAndGenerate = async () => {
    if (!selectedOpp || !selectedTemplateId) {
      showMsg("error", "Please select a template");
      return;
    }
    if (
      !projectDetails.projectContext ||
      !projectDetails.inScope ||
      !projectDetails.outOfScope
    ) {
      showMsg(
        "error",
        "Project Context, In Scope, and Out of Scope are required",
      );
      return;
    }

    setGenerating(true);
    setGenProgress({
      current: 0,
      total: 0,
      section: "Preparing…",
      percent: 0,
      startedAt: Date.now(),
    });

    try {
      // Real template list items have no `version` field -- title omits
      // the version suffix the mock used to add.
      const title = `SOW - ${selectedOpp.account_name} - ${selectedOpp.opportunity_name}`;

      // Functional update, not a replacement: startedAt is set just above
      // and the progress bar's elapsed clock reads it. A plain object here
      // drops it.
      setGenProgress((p) => ({
        ...p,
        current: 0,
        total: 1,
        section: "Creating draft…",
        percent: 5,
      }));
      // The wizard's own inputs ride on tag_values, which the backend
      // persists on the draft (sow_drafts.tag_values_json) and renders into
      // every section's prompt. Sent at create rather than generate because
      // generation runs as a detached background task that reloads the
      // draft from the database -- anything not persisted here never
      // reaches it, and would be lost again on a later regenerate.
      const d = await sowDraftApi.createDraft({
        opportunity_id: selectedOpp.id,
        template_id: Number(selectedTemplateId),
        title,
        tag_values: buildUserInputs(),
      });

      const sectionCount = d?.sections?.length || 5;
      setGenProgress((p) => ({
        ...p,
        current: 0,
        total: sectionCount,
        section: "Starting generation…",
        percent: 8,
      }));

      // No request body -- the wizard state went with createDraft above and
      // is already persisted on the draft, so the background pipeline picks
      // it up when it reloads.
      await sowDraftApi.generateDraft(d.draft_id);

      const pollInterval = 3000;
      const maxPollTime = 30 * 60 * 1000;
      const startTime = Date.now();

      const pollStatus = () =>
        new Promise((resolve, reject) => {
          // The status endpoint returns only {draft_id, status,
          // generation_error}, and the pipeline persists sections once at
          // the end -- so there is no partial progress to report. percent
          // is left null for this phase, which renders the indeterminate
          // bar plus an elapsed clock rather than a fabricated number.
          const timer = setInterval(async () => {
            try {
              if (Date.now() - startTime > maxPollTime) {
                clearInterval(timer);
                reject(new Error("Generation timed out"));
                return;
              }

              const status = await sowDraftApi.getGenerationStatus(d.draft_id);

              if (status.status === "generated") {
                clearInterval(timer);
                resolve();
              } else if (status.status === "generation_failed") {
                clearInterval(timer);
                reject(
                  new Error(status.generation_error || "Generation failed"),
                );
              } else {
                setGenProgress((p) => ({
                  ...p,
                  current: 0,
                  total: sectionCount,
                  section: "Writing sections…",
                  percent: null,
                }));
              }
            } catch (pollErr) {
              console.error("Poll error:", pollErr);
            }
          }, pollInterval);
        });

      await pollStatus();

      const generated = await sowDraftApi.getDraft(d.draft_id);

      setGenProgress((p) => ({
        ...p,
        current: sectionCount,
        total: sectionCount,
        section: "Complete",
        percent: 100,
      }));
      setDraftsMap((prev) => ({ ...prev, [selectedOpp.id]: generated }));
      setDraft(generated);
      setSelectedSectionId(generated?.sections?.[0]?.section_id || null);
      setStep(3);
      // The draft now exists server-side, so the local copy is redundant and
      // would otherwise resurrect stale inputs on a later visit. Deliberately
      // not cleared on a failed generation: the inputs are the only way back
      // to a retry until the API returns tag_values.
      clearSavedInputs(selectedOpp.id);
      showMsg("success", "SOW generated successfully!");
    } catch (e) {
      showMsg("error", getApiError(e));
    } finally {
      setGenerating(false);
      setGenProgress({
        current: 0,
        total: 0,
        section: "",
        percent: 0,
        startedAt: null,
      });
    }
  };

  const saveSection = async () => {
    if (!draft || !selectedSection) return;
    setBusy(true);
    try {
      const d = await sowDraftApi.updateSection(
        draft.draft_id,
        selectedSection.section_id,
        {
          content: selectedSection.content || "",
        },
      );
      setDraft(d);
      await loadVersionInfo();
      showMsg("success", "Section saved!");
    } catch (e) {
      showMsg("error", getApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const regenerateSection = async () => {
    if (!draft || !selectedSection) return;

    setRegenModalOpen(false);
    setGenerating(true);
    setGenProgress({
      current: 0,
      total: 1,
      section: `Regenerating ${selectedSection.title}…`,
      percent: null,
      startedAt: Date.now(),
    });

    try {
      // regenInstructions has nowhere to go server-side (no free-text
      // steering param exists on this route) -- stays local-only UI state,
      // same treatment as the wizard's resources/milestones/pricing.
      const d = await sowDraftApi.regenerateSection(
        draft.draft_id,
        selectedSection.section_id,
        true,
      );

      setGenProgress((p) => ({
        ...p,
        current: 1,
        total: 1,
        section: "Complete",
        percent: 100,
      }));
      setDraft(d);
      await loadVersionInfo();
      showMsg("success", "Section regenerated!");
    } catch (e) {
      showMsg("error", getApiError(e));
    } finally {
      setGenerating(false);
      setGenProgress({
        current: 0,
        total: 0,
        section: "",
        percent: 0,
        startedAt: null,
      });
    }
  };

  // Real versions are keyed by a "major.minor" string, not a version_id.
  const restoreVersion = async (version) => {
    if (!draft) return;
    setBusy(true);
    try {
      const d = await sowDraftApi.restoreVersion(draft.draft_id, version);
      setDraft(d);
      await loadVersionInfo();
      showMsg("success", "Version restored!");
    } catch (e) {
      showMsg("error", getApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const fetchVersions = async () => {
    if (!draft) return [];
    const res = await sowDraftApi.getVersions(draft.draft_id);
    return Array.isArray(res) ? res : res?.versions || [];
  };

  const fetchVersionDetail = async (version) => {
    if (!draft) return null;
    return sowDraftApi.getVersionDetail(draft.draft_id, version);
  };

  // No doc-type choice on the real endpoint -- it always exports the
  // current content to .docx and pushes it to the linked SF Opportunity.
  const pushToSalesforce = async () => {
    if (!draft) return { success: false, message: "No draft loaded" };
    try {
      const d = await sowDraftApi.publishToSalesforce(draft.draft_id);
      setDraft(d);
      return { success: true, message: "Published to Salesforce." };
    } catch (e) {
      return { success: false, message: getApiError(e) };
    }
  };

  const exportDocx = async () => {
    if (!draft) return;
    setBusy(true);
    try {
      const blob = await sowDraftApi.exportDocx(draft.draft_id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(draft.title || "SOW").replace(/[\\/:*?"<>|]+/g, "_")}_v${currentVersion}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      showMsg("success", "Document downloaded!");
    } catch (e) {
      showMsg("error", getApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const publishToGoogleDocs = async () => {
    if (!draft) return;
    if (!GOOGLE_CLIENT_ID) {
      showMsg("error", "Google Client ID not configured");
      return;
    }

    const title =
      prompt("Google Doc Title?", `${draft.title} v${currentVersion}`) ||
      draft.title;
    const folderId = (
      prompt("Optional: Google Drive Folder ID", "") || ""
    ).trim();

    setPublishingGdoc(true);
    try {
      const token = await getGoogleDriveAccessToken(GOOGLE_CLIENT_ID);
      const docxBlob = await sowDraftApi.exportDocx(draft.draft_id);
      const { webViewLink } = await uploadDocxAsGoogleDoc({
        accessToken: token,
        docxBlob,
        docTitle: title,
        folderId: folderId || undefined,
        supportsAllDrives: true,
      });
      setGdocLink(webViewLink || "");
      showMsg("success", "Published to Google Docs!");
      if (webViewLink)
        window.open(webViewLink, "_blank", "noopener,noreferrer");
    } catch (e) {
      showMsg("error", e?.message || String(e));
    } finally {
      setPublishingGdoc(false);
    }
  };

  const openPreview = async () => {
    if (!draft) return;
    setBusy(true);
    try {
      const res = await sowDraftApi.preview(draft.draft_id);
      setPreviewHtml(res?.html || "");
      setPreviewOpen(true);
    } catch (e) {
      showMsg("error", getApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const updateContent = (v) => {
    if (!draft || !selectedSection) return;
    setDraft({
      ...draft,
      sections: draft.sections.map((s) =>
        s.section_id === selectedSection.section_id ? { ...s, content: v } : s,
      ),
    });
  };

  const addResource = () =>
    setProjectDetails((prev) => ({
      ...prev,
      resources: [
        ...prev.resources,
        {
          role: "",
          level: "",
          location: "",
          count: 1,
          hours: 0,
          rate: 0,
          startDate: "",
          endDate: "",
          technology: "",
          domain: "",
          sellingAmount: 0,
          status: "",
        },
      ],
    }));

  const updateResource = (idx, field, value) => {
    setProjectDetails((prev) => ({
      ...prev,
      resources: prev.resources.map((r, i) => {
        if (i !== idx) return r;
        const updated = { ...r, [field]: value };
        if (field === "startDate" || field === "endDate") {
          if (updated.startDate && updated.endDate) {
            updated.hours = calculateHoursFromDates(
              updated.startDate,
              updated.endDate,
            );
          }
        }
        return updated;
      }),
    }));
  };

  const removeResource = (idx) =>
    setProjectDetails((prev) => ({
      ...prev,
      resources: prev.resources.filter((_, i) => i !== idx),
    }));

  const fetchJossResources = async () => {
    if (!selectedOpp?.sf_opportunity_id) {
      showMsg(
        "error",
        "No Salesforce Opportunity ID available for JOSS lookup",
      );
      return;
    }
    setJossLoading(true);
    try {
      const res = await sowDraftApi.fetchJossResources(
        selectedOpp.sf_opportunity_id,
      );
      if (res?.success && res?.data) {
        const mapped = mapJossToResources(res.data);
        if (mapped.length > 0) {
          setProjectDetails((prev) => ({ ...prev, resources: mapped }));
          showMsg("success", `Loaded ${mapped.length} resource(s) from JOSS`);
        } else {
          showMsg("error", "No resources found in JOSS for this opportunity");
        }
      } else {
        showMsg("error", "No data returned from JOSS API");
      }
    } catch (e) {
      showMsg("error", `JOSS: ${getApiError(e)}`);
    } finally {
      setJossLoading(false);
    }
  };

  const addMilestone = () =>
    setProjectDetails((prev) => ({
      ...prev,
      milestones: [
        ...prev.milestones,
        { name: "", deliverable: "", dueDate: "", percentage: 0 },
      ],
    }));
  const updateMilestone = (idx, field, value) =>
    setProjectDetails((prev) => ({
      ...prev,
      milestones: prev.milestones.map((m, i) =>
        i === idx ? { ...m, [field]: value } : m,
      ),
    }));
  const removeMilestone = (idx) =>
    setProjectDetails((prev) => ({
      ...prev,
      milestones: prev.milestones.filter((_, i) => i !== idx),
    }));

  const handleProposalFileSelect = (selected) => {
    if (selected.length + proposalFiles.length > 10) {
      setProposalError("Maximum 10 files allowed");
      return;
    }
    setProposalError("");
    setProposalFiles((prev) => [...prev, ...selected]);
  };

  const removeProposalFile = (idx) => {
    setProposalFiles((prev) => prev.filter((_, i) => i !== idx));
    if (proposalFiles.length <= 1) setProposalParsed(null);
  };

  const uploadAndParseProposals = async () => {
    if (!proposalFiles.length) return;
    setProposalUploading(true);
    setProposalError("");
    try {
      const result = await sowDraftApi.parseProposals(proposalFiles);
      setProposalParsed(result);
      if (result.error_count > 0) {
        const failedFiles = (result.files || [])
          .filter((f) => f.status !== "success" && f.status !== "partial")
          .map((f) => `${f.filename}: ${f.message || f.status}`)
          .join("; ");
        setProposalError(
          `${result.error_count} file(s) could not be parsed${failedFiles ? ` — ${failedFiles}` : ""}`,
        );
      }
    } catch (e) {
      setProposalError(getApiError(e));
      setProposalParsed(null);
    } finally {
      setProposalUploading(false);
    }
  };

  return {
    // navigation
    step,
    goBack,

    // opportunities (step 1)
    opps,
    oppLoading,
    oppSearch,
    setOppSearch,
    filteredOpps,
    draftsMap,
    selectOpportunity,
    loadExistingDraft,

    // project details (step 2)
    selectedOpp,
    contractType,
    templates,
    selectedTemplateId,
    setSelectedTemplateId,
    projectDetails,
    setProjectDetails,
    currentAccount,
    canCreateProject,
    newProjectModalOpen,
    setNewProjectModalOpen,
    registerProject,
    resourceTotals,
    milestoneTotals,
    grandTotal,
    addResource,
    updateResource,
    removeResource,
    addMilestone,
    updateMilestone,
    removeMilestone,
    jossLoading,
    fetchJossResources,
    proposalFiles,
    proposalParsed,
    proposalUploading,
    proposalError,
    setProposalError,
    handleProposalFileSelect,
    removeProposalFile,
    uploadAndParseProposals,
    ACCEPTED_PROPOSAL_TYPES,
    createAndGenerate,

    // draft review (step 3)
    draft,
    selectedSectionId,
    setSelectedSectionId,
    selectedSection,
    orderedSections,
    saveSection,
    updateContent,
    currentVersion,

    // generation progress
    generating,
    genProgress,

    // feedback
    busy,
    msg,
    setMsg,

    // toolbar actions
    openPreview,
    previewOpen,
    setPreviewOpen,
    previewHtml,
    exportDocx,
    publishingGdoc,
    publishToGoogleDocs,
    gdocLink,
    versionModalOpen,
    setVersionModalOpen,
    sfModalOpen,
    setSfModalOpen,
    regenModalOpen,
    setRegenModalOpen,
    regenInstructions,
    setRegenInstructions,
    regenerateSection,
    restoreVersion,
    pushToSalesforce,
    fetchVersions,
    fetchVersionDetail,
  };
}
